<template>
  <div
    v-if="variant.addons"
    class="product-addons"
    :class="{
      [$class('loading')]: state === 'loading',
      [$class('error')]: state === 'error'
    }"
  >
    <div
      class="product-addons__heading text-heading-l"
      v-text="$string('product.form.addons')"
    />

    <div
      v-for="(addon, index) of products"
      :key="index"
      class="product-addons__container"
      :class="{
        [$class('active')]: addon.isActive
      }"
      @click="handleAddOnSelection(addon)"
    >
      <div
        class="product-addons__container-inner"
        :class="{
          'product-addons__container-inner--no-description': addon && !addon.description,
        }"
      >
        <div class="product-addons__header">
          <div
            v-if="addon.title"
            class="product-addons__title text-button-m"
            v-text="addon.title"
          />

          <div
            v-if="showPriceContainer"
            class="product-addons__price-container"
          >
            <span
              v-if="addon.price?.amount > 0"
              class="product-addons__price-prefix"
              v-text="$string('product.form.addons_price_prefix')"
            />

            <product-prices
              :compare-at-price="addon.compareAtPrice?.amount"
              :price="addon.price?.amount"
              class="product-addons__price text-button-m"
              :class="{'is-free': addon.price?.amount === 0}"
            />
          </div>
        </div>

        <div
          v-if="addon.description"
          class="product-addons__body text-body-s"
          v-text="addon.description"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product addons (product-addons)
 * -----------------------------------------------------------------------------
 * Product addons.
 *
 */
import addOn from '~/graphql/queries/add-on.gql'
import { formatAddOn } from '~/helpers/format'
import ProductPrices from '~global/product-prices/product-prices'

export default {
  name: 'ProductAddons',

  components: {
    ProductPrices,
  },

  props: {
    // product: {
    //   type: [Boolean, Object],
    //   default: false,
    // },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
  },

  emits: {
    'update-addon': () => true,
  },

  data() {
    return {
      selectedAddons: [],
      products: [],
      queue: [],
      state: 'loading',
      showPriceContainer: false,
    }
  },

  computed: {

    /**
     * Compute handles of add-ons.
     * @returns {Array}
     */
    handles() {
      if (!this.variant || !this.variant.addons) {
        return []
      }

      // eslint-disable-next-line prefer-named-capture-group
      const handles = this.variant.addons.match(/handle:\s?([a-z\d-]+),/g)
      if (!handles?.length) {
        return []
      }

      return handles.map((handle) => {
        return handle
          .replace('handle:', '')
          .replace(',', '')
          .trim()
      })
    },
  },

  watch: {

    /**
     * Watch handles for change to load new handles.
     */
    handles() {
      if (!this.handles.length) {
        return
      }

      this.queryProducts()
    },

    variant: {

      /**
       * Watch for changes to the 'variant' property.
       * When 'variant' changes, hide the price container and query products.
       */
      handler() {
        this.showPriceContainer = false
        this.queryProducts()
      },
      immediate: true,
    },
  },

  mounted() {

    /**
     * Lifecycle hook called after the component is mounted.
     * Set the state to 'loaded' after 2 seconds, then show the price container after an additional 2 second.
     */
    setTimeout(() => {
      this.state = 'loaded'
      setTimeout(() => {
        this.showPriceContainer = true
      }, 2000)
    }, 2000)
  },

  methods: {

    /**
     * Emit the selected add-on.
     */
    handleAddOnSelection(addon) {
      if (addon === false) {
        return
      }

      addon.isActive = !addon.isActive

      this.$emit('update-addon', {
        id: addon.variants[0].id,
        isActive: addon.isActive,
        price: addon.variants[0].price,
      })
    },

    /**
     * Query products from GraphQL API.
     */
    async queryProducts() {
      if (!this.handles.length) {
        return
      }

      /* Update loading state array length */
      this.products = new Array(this.handles.length).fill(false)

      try {
        this.queue = this.handles.map((handle) => {
          return cnvs.Query({
            query: addOn,
            variables: {
              country: this.$variable('store.country'),
              handle,
              language: this.$variable('store.language'),
            },
          })
        })

        const response = await Promise.allSettled(this.queue)

        const products = response.map((promise) => {
          if (promise.status !== 'fulfilled') {
            return false
          }

          if (promise.value.product === null) {
            return false
          }

          return formatAddOn(promise.value)
        }).filter(Boolean)

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        /**
         * If no products returned then error.
         */
        if (!products.length) {
          this.state = 'error'
          return
        }

        this.products = products
        this.state = 'ready'
        this.showPriceContainer = true

      } catch (error) {
        this.state = 'error'
      }
    },
  },
}
</script>

<style lang="scss">
@import './product-addons';
</style>
